import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateIt } from "@/lib/format";
import { exportCsv } from "@/lib/csv";
import { PRIVACY, SOURCES, CONTACT_ROLE_CLS } from "@/lib/constants";
import { PageHeader, PrivacyBadge, EmptyState, Field } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Download, Search, Pencil, Trash2, Phone, Mail, Ban } from "lucide-react";

const EMPTY = { name: "", company: "", phone: "", email: "", source: "Passaparola", assigned_to: "", privacy_status: "GREEN", last_contact_date: "", outcome: "", next_action: "", next_followup_date: "", notes: "" };

export default function Contacts() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [privacy, setPrivacy] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [linkedProps, setLinkedProps] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/contacts").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    if (isAdmin) api.get("/auth/users").then((r) => setUsers(r.data));
  }, [isAdmin, load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (privacy !== "all" && r.privacy_status !== privacy) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!`${r.name} ${r.company} ${r.email} ${r.phone}`.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [rows, search, privacy]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setLinkedProps([]); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c); setForm({ ...EMPTY, ...c, assigned_to: c.assigned_to || "" }); setLinkedProps([]); setOpen(true);
    api.get(`/contacts/${c.id}`).then((r) => setLinkedProps(r.data.properties || [])).catch(() => {});
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (editing) await api.put(`/contacts/${editing.id}`, payload);
      else await api.post("/contacts", payload);
      toast.success(editing ? "Contatto aggiornato" : "Contatto creato");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/contacts/${delItem.id}`); toast.success("Contatto eliminato"); setDelItem(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const csv = () => exportCsv("contatti_investart.csv", filtered, [
    { label: "ID", key: "id" }, { label: "Nome", key: "name" }, { label: "Azienda", key: "company" },
    { label: "Telefono", key: "phone" }, { label: "Email", key: "email" }, { label: "Fonte", key: "source" },
    { label: "Assegnato a", key: "assigned_to_name" }, { label: "Privacy", key: "privacy_status" },
    { label: "Ultimo contatto", value: (r) => dateIt(r.last_contact_date) }, { label: "Esito", key: "outcome" },
    { label: "Prossima azione", key: "next_action" }, { label: "Prossimo follow-up", value: (r) => dateIt(r.next_followup_date) },
  ]);

  return (
    <div data-testid="contacts-page">
      <PageHeader title="Contatti" subtitle={`${filtered.length} contatti`}>
        <Button variant="outline" onClick={csv} data-testid="export-csv-contacts"><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button onClick={openNew} data-testid="new-contact-btn"><Plus className="h-4 w-4 mr-2" />Nuovo Contatto</Button>
      </PageHeader>

      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca per nome, azienda, email..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contacts-search" />
        </div>
        <Select value={privacy} onValueChange={setPrivacy}>
          <SelectTrigger className="w-full sm:w-56" data-testid="privacy-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati privacy</SelectItem>
            {Object.keys(PRIVACY).map((k) => <SelectItem key={k} value={k}>{PRIVACY[k].label} — {PRIVACY[k].desc}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Contatti</TableHead>
                <TableHead>Fonte</TableHead>
                <TableHead>Assegnato</TableHead>
                <TableHead>Privacy</TableHead>
                <TableHead>Prossimo Follow-up</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const allowed = PRIVACY[c.privacy_status]?.allowed;
                return (
                  <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-800">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.company || "—"}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!allowed}
                          title={allowed ? "Chiama" : "Contatto bloccato dalla privacy"}
                          onClick={() => allowed && (window.location.href = `tel:${c.phone}`)}
                          data-testid={`call-${c.id}`}>
                          {allowed ? <Phone className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5 text-rose-400" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!allowed}
                          title={allowed ? "Email" : "Contatto bloccato dalla privacy"}
                          onClick={() => allowed && (window.location.href = `mailto:${c.email}`)}
                          data-testid={`email-${c.id}`}>
                          <Mail className="h-3.5 w-3.5" />
                        </Button>
                        <span className="text-xs text-muted-foreground ml-1">{c.phone}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{c.source}</TableCell>
                    <TableCell className="text-sm">{c.assigned_to_name}</TableCell>
                    <TableCell><PrivacyBadge status={c.privacy_status} testid={`privacy-badge-${c.id}`} /></TableCell>
                    <TableCell className="text-sm">{dateIt(c.next_followup_date)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => setDelItem(c)} data-testid={`delete-contact-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {!loading && filtered.length === 0 && <EmptyState>Nessun contatto trovato.</EmptyState>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica Contatto" : "Nuovo Contatto"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label="Nome *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></Field>
            <Field label="Azienda"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
            <Field label="Telefono"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Fonte di acquisizione">
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Stato privacy">
              <Select value={form.privacy_status} onValueChange={(v) => setForm({ ...form, privacy_status: v })}>
                <SelectTrigger data-testid="contact-privacy"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(PRIVACY).map((k) => <SelectItem key={k} value={k}>{PRIVACY[k].label} — {PRIVACY[k].desc}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {isAdmin && (
              <Field label="Assegnato a">
                <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona assegnatario" /></SelectTrigger>
                  <SelectContent>
                    {users.filter((u) => u.assignable !== false && u.role !== "admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Ultimo contatto"><Input type="date" value={form.last_contact_date || ""} onChange={(e) => setForm({ ...form, last_contact_date: e.target.value })} /></Field>
            <Field label="Esito"><Input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} /></Field>
            <Field label="Prossima azione"><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} /></Field>
            <Field label="Prossimo follow-up"><Input type="date" value={form.next_followup_date || ""} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} /></Field>
            <Field label="Note" className="sm:col-span-2"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            {editing && (
              <Field label="Immobili collegati" className="sm:col-span-2">
                <div className="space-y-2" data-testid="contact-linked-properties">
                  {linkedProps.length === 0 && <div className="text-sm text-muted-foreground">Nessun immobile collegato.</div>}
                  {linkedProps.map((pr) => (
                    <a key={pr.id} href={`/immobili/${pr.id}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 hover:bg-muted/50" data-testid={`contact-linked-property-${pr.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{pr.address}</div>
                        <div className="text-xs text-muted-foreground truncate">{pr.property_type}{pr.city ? ` · ${pr.city}` : ""} · {pr.status}</div>
                      </div>
                      <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${CONTACT_ROLE_CLS[pr.role] || CONTACT_ROLE_CLS["Referente"]}`}>{pr.role || "Proprietario"}</span>
                    </a>
                  ))}
                </div>
              </Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-contact-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il contatto?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione eliminerà "{delItem?.name}" in modo permanente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-contact">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
